/* eslint-disable no-console */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { useEffect, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faCirclePlay } from '@fortawesome/pro-regular-svg-icons/faCirclePlay';
import { Deal, Plan } from '../../types/plans';
import { useIntl } from 'react-intl';
import ModalVideo from 'react-modal-video';
import { MonthlyPrice } from './MonthlyPrice';
import {
  useReducedPrice,
  formatPrice,
  trackPlanSelection,
  getCurrencySymbol,
  getPlanHref,
} from './utils';
import { useRouter } from 'next/router';

import 'react-modal-video/scss/modal-video.scss';
import {
  text0,
  text3,
  text5,
  textDarkBlue,
} from '@osmosis/tailwind-ui/styles/text';

type MatrixButtonBlockProps = {
  plan?: Plan;
  upsellPlan?: Plan;
  name?: string;
  originName?: string;
  color: string;
  deal?: Deal;
  position: 'top' | 'bottom';
  displayDescription?: string;
  monthlyDailyPrice: string | null;
  size?: string;
};

export enum PlanName {
  BasicSciences = 'Basic Sciences',
  OsmosisSuite = 'Osmosis Suite',
}

const videoIds: Record<PlanName, string> = {
  [PlanName.BasicSciences]: 'Sy6WJLC2VBY',
  [PlanName.OsmosisSuite]: 'eg982x08oPE',
};

export const trackingKeys: Record<PlanName, string> = {
  [PlanName.BasicSciences]: 'basic_sciences_video_played',
  [PlanName.OsmosisSuite]: 'osmosis_suite_video_played',
};

export const plansPlayedVideosLSKey = 'plans_played_videos';

/**
 * Matrix Button Block component.
 * @param props
 * @param {Plan} props.plan - Plan object.
 * @param {PlanName} props.name - Name of the plan.
 * @param {string} props.color - Color of the plan.
 * @param {string} props.href - URL to redirect.
 * @param {string} props.variant - Variant of the button block.
 * @class MatrixButtonBlock
 * @returns {JSX.Element} Returns Matrix Button Block component.
 */
export const MatrixButtonBlock = (props: MatrixButtonBlockProps) => {
  const {
    plan,
    upsellPlan,
    name,
    originName,
    color,
    deal,
    position,
    displayDescription,
    monthlyDailyPrice,
    size,
  } = props;
  const router = useRouter();
  const intl = useIntl();
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const isDeal = !!(deal && Object.keys(deal).length > 0);
  const nameToMap = originName || name;
  const ariaDescribedByID = `${(name ?? '')
    .toLowerCase()
    .replace(/\s+/g, '-')}`;
  useEffect(() => {
    const playedVideosStr = localStorage.getItem(plansPlayedVideosLSKey);

    if (!playedVideosStr) {
      const playedVideos: Record<string, boolean> = Object.values(
        trackingKeys,
      ).reduce((acc, key) => {
        acc[key] = false;

        return acc;
      }, {} as Record<string, boolean>);

      localStorage.setItem(
        plansPlayedVideosLSKey,
        JSON.stringify(playedVideos),
      );
    }
  }, []);

  const reducedPrice = useReducedPrice(isDeal, plan, deal);

  const openVideo = () => {
    setVideoModalOpen(true);
    const playedVideosStr = localStorage.getItem(
      plansPlayedVideosLSKey,
    ) as string;
    const playedVideos: Record<string, boolean> = JSON.parse(playedVideosStr);

    playedVideos[trackingKeys[nameToMap as PlanName]] = true;

    localStorage.setItem(plansPlayedVideosLSKey, JSON.stringify(playedVideos));
  };

  const onBuyClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (!plan) {
      console.log('No plan ~ no plan selection tracking');

      return; // Return early if plan is not defined
    }
    await trackPlanSelection(plan, deal);
    const href = plan ? getPlanHref(plan, deal, upsellPlan) : '#';

    router.push(href);
  };
  const dealAmt = Math.round(plan?.currency.origUnitAmount ?? 0);
  const planAmt = Math.round(plan?.currency.unitAmount ?? 0);

  return (
    <>
      <div className="grid-item mx-auto">
        {position !== 'bottom' && (
          <P
            className="mx-1 mb-8 text-center font-normal"
            style={{ fontSize: '14px' }}
          >
            {displayDescription}
          </P>
        )}

        <Box data-testid="price-slot-matrix">
          {/* Show the monthly */}

          {monthlyDailyPrice && plan?.currency && (
            <MonthlyPrice
              monthlyDailyPrice={monthlyDailyPrice}
              plan={plan}
              deal={deal}
            />
          )}

          <Box
            data-testid="annual-price"
            style={{
              width: size === 'small' ? '125px' : '175px',
              margin: '0 auto',
            }}
          >
            {/* Show the deal strikeout price */}
            {isDeal && !monthlyDailyPrice && reducedPrice > 0 && (
              <P
                data-testid={`matrix-plan-base-price-${plan?.code}`}
                className={`ml-7 text-center text-xs font-medium ${text3} line-through ${
                  size === 'small' ? 'pb-2 pl-2' : ''
                }`}
                style={{
                  float: 'left',
                  width: 'fit-content',
                  fontSize: '.80rem',
                }}
              >
                {getCurrencySymbol(plan?.currency.currency || 'USD')}
                {formatPrice(dealAmt || planAmt || 0)}
              </P>
            )}
            {/* Show the actual price */}
            {/* if non-deal, then it should be centered */}
            {!monthlyDailyPrice && reducedPrice > 0 && (
              <P
                data-testid={`matrix-plan-offered-price-${plan?.code}`}
                className={`pb-4 font-bold ${text5} ${
                  isDeal ? 'mr-7' : 'mx-auto block text-center'
                }`}
                style={{
                  float: isDeal ? 'right' : 'none',
                  width: 'fit-content',
                  fontSize: '1.25rem',
                  marginLeft: isDeal ? undefined : 'auto',
                  marginRight: isDeal ? undefined : 'auto',
                }}
              >
                {getCurrencySymbol(plan?.currency.currency || 'USD')}
                {!isDeal ? formatPrice(planAmt) : formatPrice(reducedPrice)}
              </P>
            )}
            <div style={{ clear: 'both' }} />
          </Box>
        </Box>

        <Button
          key={name}
          className={`bg-${color} max-w-3/5 mx-auto mb-7 min-w-[140px] rounded-full ${text0}`}
          data-testid={`matrix-plan-price-${plan?.code}`}
          as="button"
          onClick={onBuyClick}
          disabled={!plan}
          aria-describedby={ariaDescribedByID}
        >
          <P className="text-base leading-6">
            {intl.formatMessage({
              id: 'matrix.buy_now',
              defaultMessage: 'Buy Now',
            })}
          </P>
        </Button>

        {Object.keys(videoIds).includes(nameToMap as PlanName) &&
          position !== 'bottom' && (
            <Box className="max-w-[95%] overflow-hidden pb-4 text-center">
              <Button
                className={`text-normal mr-1 inline-block cursor-pointer font-normal ${textDarkBlue}`}
                onClick={openVideo}
                style={{
                  fontSize: '.80rem',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                tabIndex={0}
                aria-label={intl.formatMessage(
                  {
                    id: 'matrix.watch_why',
                    defaultMessage: 'Watch why {planName} - launch video',
                  },
                  { planName: name },
                )}
                data-testid={`matrix-plan-why-${plan?.code}`}
              >
                {intl.formatMessage(
                  {
                    id: 'matrix.watch_why',
                    defaultMessage: 'Watch why {planName}',
                  },
                  { planName: name },
                )}
                <Icon
                  icon={faCirclePlay}
                  size="sm"
                  className="ml-1 inline-block align-middle text-blue-500"
                  aria-hidden="true"
                />
              </Button>
            </Box>
          )}
      </div>
      <ModalVideo
        channel="youtube"
        youtube={{ mute: 0, autoplay: 0 }}
        isOpen={isVideoModalOpen}
        videoId={videoIds[nameToMap as PlanName]}
        onClose={() => setVideoModalOpen(false)}
        aria-label={intl.formatMessage({
          id: 'matrix.video_modal',
          defaultMessage: 'Video modal',
        })}
      />
    </>
  );
};

export default MatrixButtonBlock;
